/**
 * MYAC-1313 — `views/pilotage_opening`, as the browser reads it.
 *
 * Tolerant-reader DTOs of the projected view (source of truth:
 * `core_models_services/src/models/pilotage_opening/views.py`). Plain interfaces,
 * every field optional-or-defaulted: a field added server-side must degrade in a
 * deployed browser, never crash the page whose job is to calm the user down.
 */

/** `{en: "...", fr: "..."}` — LocalizedText's serialized shape.
 *  Re-exported from `@/utils/localizedText`, where the platform-wide chain now
 *  lives (MYAC-1381 gave it a second reader, the referential catalogue). */
export type { LocalizedTextMap } from '@/utils/localizedText'
export { resolveLocalized } from '@/utils/localizedText'
import type { LocalizedTextMap } from '@/utils/localizedText'

export interface OpeningReplyDoc {
  label?: LocalizedTextMap
  prompt?: LocalizedTextMap
  /** 'where_to_start' | 'expects_no' | 'open' — unknown kinds render as plain chips. */
  kind?: string
}

export interface PilotageOpeningDoc {
  message?: LocalizedTextMap
  replies?: OpeningReplyDoc[]
  cause?: string
  /** The EVENT's time, not the generation's — what the bubble is dated by. */
  event_at?: { toDate(): Date } | Date | null
  generated_at?: { toDate(): Date } | Date | null
  stale?: boolean
  empty_space?: boolean
}

export interface PilotageOpeningView {
  qms?: PilotageOpeningDoc | null
  td?: PilotageOpeningDoc | null
  view_version?: number
}

/** Firestore returns `Timestamp`s; a fixture may hand a `Date`. One door —
 * and a TOLERANT one (review #735 r3): a malformed `event_at` (a string, a
 * number, an object with no `toDate`) degrades to null, never a throw in the
 * middle of rendering the greeting. */
export function openingDate(value: PilotageOpeningDoc['event_at']): Date | null {
  if (!value) return null
  if (value instanceof Date) return value
  try {
    const parsed = typeof (value as { toDate?: unknown }).toDate === 'function'
      ? (value as { toDate(): Date }).toDate()
      : null
    return parsed instanceof Date && !Number.isNaN(parsed.getTime()) ? parsed : null
  } catch {
    return null
  }
}

/**
 * MYAC-1313 — the first-turn marker: `__OPENING__{"message":"…"}\n<user text>`.
 * The chat agent's `bmc_apply_opening_reference` strips it and tells the model
 * what it "just said" (client-supplied session state is 403'd — the user's own
 * message is the one channel the client owns, the `__FOCUS__` precedent).
 */
export const OPENING_PREFIX = '__OPENING__'
